import { useCallback, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { useVisibilityAwarePoll } from './useVisibilityAwarePoll';
import type { AgentStats, ClaimResult } from '@/lib/bots/edge';
import { claimBot, releaseBot } from '@/lib/bots/edge';
import { deriveMotion, frontOf, HIST, type Motion, type Sample } from '@/lib/bots/telemetry';

export type FleetStatus = 'loading' | 'ok' | 'not-configured' | 'error';

/**
 * Samples further apart than this many polls are no longer one continuous
 * observation — a hidden tab, a stalled poll, or a remount happened in between.
 * Two allows for ordinary jitter and one dropped tick.
 */
const MAX_SAMPLE_GAP_POLLS = 2;

/**
 * Live edge-fleet poll for the Position Tracker bot zones: the agent list +
 * per-agent climb/ETA motion (from a rolling queue-history ref) + the control
 * actions (pause/resume/kill/claim/release), each refreshing on success. The
 * relay scopes mutating control to managers/admins or the agent's claimer, so a
 * trader's pause/kill on a bot they don't own simply 403s.
 *
 * #334 — the read throttles in a hidden tab. Nothing here decides anything: the
 * agents run on the edge's own clock and this is their scoreboard, so a hidden
 * tab reading them at 12s costs nobody a decision, and wake fires an immediate
 * read before the operator can click a control.
 *
 * The motion history is the one thing throttling could have corrupted, so it
 * restarts across a gap. `deriveMotion` treats a run of known-front samples as
 * one continuous observation of one quote, and it detects a cancel/replace only
 * by seeing an unknown-front (-1) sample land inside the gap. A re-quote that
 * begins and ends between two hidden-cadence samples leaves no such marker: the
 * run would bridge it, anchor on the OLD quote's position, and report a climb
 * and fill-ETA that never happened. Discarding the history whenever samples are
 * further apart than MAX_SAMPLE_GAP_POLLS is the honest answer — a wake shows
 * "—" until two fresh samples exist, instead of a fabricated number.
 */
export function useEdgeFleet(opts?: { enabled?: boolean; pollMs?: number; orgId?: string | null }): {
  agents: AgentStats[];
  motion: Map<string, Motion>;
  status: FleetStatus;
  dryRun: boolean | null;
  error: string | null;
  refresh: () => void;
  pause: (id: string) => Promise<boolean>;
  resume: (id: string) => Promise<boolean>;
  kill: (id: string) => Promise<boolean>;
  claim: (id: string, ticker?: string) => Promise<ClaimResult>;
  release: (id: string) => Promise<boolean>;
} {
  const enabled = opts?.enabled ?? true;
  const pollMs = opts?.pollMs ?? 2000;
  // Admin org-picker: pass the viewed org so an admin sees that org's fleet. The
  // relay honors ?orgId for admins only (view scope) — it never widens control
  // (interaction is membership-gated server-side), so it's harmless for others.
  const orgId = opts?.orgId ?? null;
  const [agents, setAgents] = useState<AgentStats[]>([]);
  const [status, setStatus] = useState<FleetStatus>('loading');
  const [dryRun, setDryRun] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const histRef = useRef<Map<string, Sample[]>>(new Map());
  const lastSampleMsRef = useRef(0);

  const ingest = useCallback(
    (list: AgentStats[]) => {
      const now = Date.now();
      const m = histRef.current;
      const sinceLast = now - lastSampleMsRef.current;
      if (lastSampleMsRef.current > 0 && sinceLast > pollMs * MAX_SAMPLE_GAP_POLLS) m.clear();
      lastSampleMsRef.current = now;
      const liveIds = new Set(list.map((a) => a.id));
      for (const id of [...m.keys()]) if (!liveIds.has(id)) m.delete(id);
      for (const a of list) {
        const arr = m.get(a.id) ?? [];
        arr.push({ ms: now, front: frontOf(a) });
        if (arr.length > HIST) arr.splice(0, arr.length - HIST);
        m.set(a.id, arr);
      }
      setAgents(list);
      setStatus('ok');
      setError(null);
    },
    [pollMs],
  );

  // `orgId` in the deps is load-bearing beyond memoization: the admin org
  // picker changes it, which re-arms the loop, drops the previous org's
  // in-flight read, and refetches immediately instead of showing the old
  // fleet until the next tick.
  const read = useCallback(
    async (signal: AbortSignal) => {
      try {
        const res = await apiFetch(
          orgId ? `/api/edge/agents?orgId=${encodeURIComponent(orgId)}` : '/api/edge/agents',
        );
        if (signal.aborted) return;
        if (res.status === 503) {
          const b = await res.json().catch(() => ({}));
          if (signal.aborted) return;
          setStatus('not-configured');
          setError(b.error ?? 'edge not configured');
          setDryRun(null);
          return;
        }
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          if (signal.aborted) return;
          setStatus('error');
          setError(b.error ?? `HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        if (signal.aborted) return;
        setDryRun(typeof data.dryRun === 'boolean' ? data.dryRun : null);
        ingest(Array.isArray(data.agents) ? data.agents : []);
      } catch (e) {
        if (signal.aborted) return;
        setStatus('error');
        setError((e as Error).message);
      }
    },
    [ingest, orgId],
  );

  const { refresh } = useVisibilityAwarePoll(read, pollMs, { enabled });

  const motion = useMemo(() => {
    const out = new Map<string, Motion>();
    for (const a of agents) out.set(a.id, deriveMotion(histRef.current.get(a.id)));
    return out;
  }, [agents]);

  const post = useCallback(
    async (path: string, method: 'POST' | 'DELETE'): Promise<boolean> => {
      try {
        const res = await apiFetch(path, { method });
        if (res.ok) void refresh();
        return res.ok;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  const claim = useCallback(
    async (id: string, ticker?: string) => {
      const r = await claimBot(id, ticker);
      // ALWAYS refresh — even a lost claim (409) must update the list so the
      // real owner shows, instead of a stale Claim click silently no-op'ing.
      void refresh();
      return r;
    },
    [refresh],
  );
  const release = useCallback(
    async (id: string) => {
      const ok = await releaseBot(id);
      if (ok) void refresh();
      return ok;
    },
    [refresh],
  );

  return {
    agents,
    motion,
    status,
    dryRun,
    error,
    refresh,
    pause: (id) => post(`/api/edge/agents/${encodeURIComponent(id)}/pause`, 'POST'),
    resume: (id) => post(`/api/edge/agents/${encodeURIComponent(id)}/resume`, 'POST'),
    kill: (id) => post(`/api/edge/agents/${encodeURIComponent(id)}`, 'DELETE'),
    claim,
    release,
  };
}
